import axios from 'axios';
import React, { useEffect, useState } from 'react';

function TrendingProducts() {
    const [searchQuery, setSearchQuery] = useState('');
    const [products, setProducts] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [trendingProducts, setTrendingProducts] = useState([]);

    // Fetch products for search
    const fetchProducts = async (query = '') => {
        try {
            setLoading(true);
            const response = await axios.get(
                `${process.env.REACT_APP_BASEURL}/product/improved-product/generic_search/`,
                {
                    params: {
                        search: query,
                        isSearch: true,
                    },
                }
            );
            setProducts(response.data.results || []);
        } catch (error) {
            console.error("Error fetching products:", error);
        } finally {
            setLoading(false);
        }
    };

    // Fetch current trending products - FIXED URL
    const fetchTrendingProducts = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_BASEURL}/product/trending-products/`, // ✅ Fixed: added api/v1
                // {
                //     headers: {
                //         'Authorization': `Token ${localStorage.getItem('adminAuth')}`
                //     }
                // }
            );
            setTrendingProducts(response.data.products_detail || []);
        } catch (error) {
            console.error("Error fetching trending products:", error);
        }
    };

    // Add selected products to trending - FIXED URL & ID handling
    const addToTrending = async () => {
        if (selectedProducts.length === 0) {
            alert('Please select products first!');
            return;
        }

        if (selectedProducts.length > 50) {
            alert('Maximum 50 products allowed!');
            return;
        }

        try {
            setLoading(true);
            
            // ✅ Convert to numbers if they are strings (matches your curl)
            const productIds = selectedProducts.map(p => Number(p));
            
            const response = await axios.post(
                `${process.env.REACT_APP_BASEURL}/product/trending-products/`, // ✅ Fixed: added api/v1
                {
                    products: productIds  // ✅ Send as numbers: [11255, 11265]
                },
                {
                    headers: {
                        'accept': 'application/json',
                        'Authorization': `Token ${localStorage.getItem('adminAuth')}`,
                        'Content-Type': 'application/json',
                    }
                }
            );
            
            setTrendingProducts(response.data.products_detail);
            setSelectedProducts([]);
            alert('Products added to trending successfully!');
            fetchTrendingProducts(); // Refresh list
        } catch (error) {
            console.error("Error adding to trending:", error.response?.data || error.message);
            alert(`Failed: ${error.response?.data?.products?.[0] || 'Unknown error'}`);
        } finally {
            setLoading(false);
        }
    };

    // Handle product selection
    const toggleProductSelection = (productId) => {
        setSelectedProducts(prev =>
            prev.find(id => id == productId)  // ✅ Loose equality for string/number
                ? prev.filter(id => id != productId)
                : [...prev, productId]
        );
    };

    // Debug product IDs (use this temporarily)
    console.log('All products:', products.map(p => ({id: p.id, type: typeof p.id})));
    console.log('Selected:', selectedProducts);

    // Debounced search
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            fetchProducts(searchQuery);
        }, 300);
        return () => clearTimeout(timeoutId);
    }, [searchQuery]);

    // Initial load
    useEffect(() => {
        fetchTrendingProducts();
    }, []);

    return (
        <div className="max-w-6xl mx-auto p-6">
            <h1 className="text-3xl font-bold mb-8">Trending Products Manager</h1>

            {/* Search Section */}
            <div className="mb-8 p-6 bg-white rounded-lg shadow-md">
                <h2 className="text-2xl font-semibold mb-4">Search Products</h2>
                <input
                    type="text"
                    placeholder="Search products..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />

                {loading ? (
                    <p>Searching...</p>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {products.map((product) => {
                            // ✅ Fixed: Removed invalid console.log from JSX
                            console.log(`Product ${product.id}:`, typeof product.id, product.id);
                            return (
                                <div
                                    key={product.id}
                                    className={`p-4 border rounded-lg cursor-pointer transition-all ${
                                        selectedProducts.includes(product.id)
                                            ? 'border-blue-500 bg-blue-50'
                                            : 'border-gray-200 hover:border-gray-400'
                                    }`}
                                    onClick={() => toggleProductSelection(product.id)}
                                >
                                    <div className="flex items-center space-x-3">
                                        <input
                                            type="checkbox"
                                            checked={selectedProducts.includes(product.id)}
                                            onChange={() => toggleProductSelection(product.id)}
                                            className="w-5 h-5 text-blue-600"
                                        />
                                        <div>
                                            <h3 className="font-semibold">{product.name}</h3>
                                            <p className="text-gray-600 text-sm">{product.brand}</p>
                                            <p className="text-xs text-gray-400">ID: {product.id}</p>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Selection Summary */}
            <div className="mb-8 p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="font-semibold">
                    Selected: {selectedProducts.length}/50 products
                    {selectedProducts.length > 0 && (
                        <span className="ml-2 text-sm text-gray-600">
                            IDs: {selectedProducts.join(', ')}
                        </span>
                    )}
                </p>
                <button
                    onClick={addToTrending}
                    disabled={loading || selectedProducts.length === 0}
                    className="mt-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
                >
                    {loading ? 'Adding...' : `Add ${selectedProducts.length} to Trending`}
                </button>
            </div>

            {/* Current Trending Products */}
            <div className="p-6 bg-white rounded-lg shadow-md">
                <h2 className="text-2xl font-semibold mb-4">Current Trending Products ({trendingProducts.length})</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {trendingProducts.map((product) => (
                        <div key={product.id} className="p-4 border rounded-lg">
                            <h3 className="font-semibold">{product.equip_name}</h3>
                            <p className="text-gray-600 text-sm">{product.brand}</p>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default TrendingProducts;
